import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import GoogleReviews from "../components/GoogleReviews";
import FAQ from "../components/FAQ";
import { usePageMeta, ORG_JSON_LD, productJsonLd } from "../lib/usePageMeta";
import { Linkedin, Search, Target, Users, TrendingUp } from "lucide-react";

const META = {
  seo: {
    title: "LinkedIn Profile Optimisation · UK Experts · CV Folks",
    description:
      "Get found by recruiters. Our UK writers optimise your LinkedIn profile — headline, About, experience, skills — for search visibility and hiring-manager appeal.",
    keywords: "linkedin optimisation uk, linkedin profile writer, linkedin makeover",
  },
  jsonLd: [
    ORG_JSON_LD,
    productJsonLd({ id: "standard-package", name: "Standard Package", price: 95, description: "Standard CV + Cover Letter + LinkedIn profile makeover." }),
    productJsonLd({ id: "premium-package", name: "Premium Package", price: 155, description: "Premium CV + Cover Letter + LinkedIn makeover + career consultation call." }),
  ],
};

const DELIVERABLES = [
  { icon: Search, title: "Keyword-rich headline", text: "Rewritten to appear in recruiter searches for your target role." },
  { icon: Target, title: "About section", text: "A compelling 3-paragraph story that positions you clearly and confidently." },
  { icon: Users, title: "Experience rewrite", text: "Achievement-led, quantified summaries under each role." },
  { icon: TrendingUp, title: "Skills & endorsements", text: "The right 30-skill mix to unlock LinkedIn's recruiter filters." },
];

export default function ServicesLinkedInPage() {
  usePageMeta(META);
  return (
    <>
      <Navbar />
      <main data-testid="services-linkedin-page" className="pt-24 md:pt-32 bg-background">
        <section className="max-w-6xl mx-auto px-6 md:px-10 pb-14 md:pb-20">
          <p className="label-eyebrow">LinkedIn Profile Optimisation</p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mt-3 leading-[1.05] max-w-3xl">
            Get discovered by UK recruiters.
          </h1>
          <p className="text-base md:text-lg text-foreground/70 mt-5 max-w-2xl">
            A LinkedIn profile that actually pulls opportunities in — search-visible,
            recruiter-focused, written by a UK expert.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/checkout/standard-package"
              className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-7 py-3.5 text-base font-semibold hover:opacity-90 shadow-sm"
            >
              <Linkedin className="h-5 w-5" /> Standard Package · £95
            </Link>
            <Link
              to="/checkout/premium-package"
              className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3.5 text-sm font-semibold hover:border-brand hover:text-brand"
            >
              Premium Package · £155
            </Link>
          </div>
          <p className="text-xs text-foreground/60 mt-4">
            LinkedIn optimisation is included in every Package (Standard &amp; Premium).
          </p>
        </section>

        <section className="bg-muted/30 py-20 md:py-28">
          <div className="max-w-6xl mx-auto px-6 md:px-10">
            <p className="label-eyebrow text-center">What&apos;s included</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-center mt-3">
              Every section, rewritten for recruiters.
            </h2>
            <div className="mt-12 grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
              {DELIVERABLES.map((d) => (
                <div
                  key={d.title}
                  data-testid={`linkedin-deliverable-${d.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="rounded-2xl border border-border bg-card p-6 flex gap-4"
                >
                  <div className="h-10 w-10 rounded-full bg-brand/10 text-brand flex items-center justify-center shrink-0">
                    <d.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg font-bold">{d.title}</h3>
                    <p className="text-sm text-foreground/70 mt-1">{d.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <GoogleReviews />
        <FAQ />
      </main>
      <Footer />
    </>
  );
}
